import {
  Button,
  type FrameArtwork,
  FrameDrawer,
  type FrameLayout,
} from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { consolidateFrames } from '@utils/product';
import { resolveShopifyAdminProductId } from '@utils/shopifyIds';
import { useTranslation } from 'next-i18next';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { storefrontClient } from '../apollo/shopify/storefrontClient';
import { FF_FRAMING_PREVIEWER_RELEASE_PAGE } from '../constants/featureFlags';
import { withFramingChoice } from '../features/checkout/utils/framingQuery';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import type {
  FramingArtwork,
  FramingArtworksResponse,
} from '../pages/api/storyblok/framing-artworks';
import type { FramingPreviewerStoryblok } from '../types/generated-storyblok';
import type { CurrencyCode } from '../types/Locations';
import type { Frame, FrameOptionConsolidated } from '../types/Products';
import type { ShopifyProductPurchaseDetails } from '../types/Purchasing';
import { getLocalisedProductUrl } from '../utils/variants';
import {
  type FrameDraft,
  useFrameDraft,
} from './Checkout/Framing/hooks/useFrameDraft';
import {
  buildFrameGroups,
  selectFrameGroupOption,
} from './Checkout/Framing/utils/frameGroups';
import { ContentBlockTitle } from './ContentBlockTitle';

const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);
const noop = () => undefined;

type Details = ShopifyProductPurchaseDetails | null;

export const toFrameOptions = (details: ShopifyProductPurchaseDetails) =>
  consolidateFrames({
    compositeImageProductShopifyId: details.shopifyId,
    frameOptions: details.frames,
    frameRecommendedLabel: details.frameRecommendedLabel,
    frameRecommendedTitle: details.frameRecommendedTitle,
    selection: [{ id: details.shopifyId, quantity: 1 }],
  });

/**
 * `FrameDrawer` driven by the shared checkout draft and pricing helpers. Mount
 * it per product (key it) — the draft seeds once.
 */
export const FrameSelection = ({
  artworks,
  currencyCode,
  currentSelection,
  frameOptions,
  layout,
  onArtworkChange,
  onClose,
  productImage,
  renderActions,
  selectedArtworkId,
}: {
  artworks: FrameArtwork[];
  currencyCode?: CurrencyCode;
  currentSelection?: Frame | null;
  frameOptions: FrameOptionConsolidated[];
  layout: FrameLayout;
  onArtworkChange?: (artworkId: string) => void;
  onClose?: () => void;
  /** The product's Shopify image, shown when nothing framed is selected. */
  productImage?: string | null;
  renderActions?: (draft: FrameDraft) => ReactNode;
  selectedArtworkId: string;
}) => {
  const { t } = useTranslation('framing');
  const draft = useFrameDraft({ frameOptions, currentSelection });
  // Whether the last choice was a mounting, which the preview then shows.
  const [showMounting, setShowMounting] = useState(false);

  const artwork = artworks.find(({ id }) => id === selectedArtworkId);
  // After a mounting choice, that frame variant's own Shopify image. Otherwise
  // the selected frame product's second image (the work in that frame; the
  // first is its thumbnail). Then the product's own Shopify image, then the
  // Storyblok artwork.
  const mountingImage = showMounting
    ? draft.mountingOptions.find(
        ({ mounting }) => mounting === draft.selectedMounting,
      )?.image
    : null;
  const framedImage =
    mountingImage ||
    frameOptions.find(({ title }) => title === draft.selectedStyleTitle)
      ?.images[1]?.src;
  const unframedImage = productImage || artwork?.thumbnail.src;
  const previewImage =
    framedImage || unframedImage
      ? {
          src: (framedImage || unframedImage) as string,
          alt: artwork?.title ?? '',
        }
      : undefined;

  return (
    <FrameDrawer
      actions={renderActions?.(draft)}
      artworks={artworks}
      currencyCode={currencyCode}
      groups={buildFrameGroups(t, frameOptions, draft)}
      layout={layout}
      onArtworkChange={onArtworkChange}
      onClose={onClose}
      onSelect={(groupId, optionId) => {
        if (groupId !== 'glazing') setShowMounting(groupId === 'mounting');
        selectFrameGroupOption(draft, groupId, optionId);
      }}
      previewImage={previewImage}
      selectedArtworkId={selectedArtworkId}
      total={draft.framePrice}
    />
  );
};

const useArtworks = (shopifyId: string) => {
  const { i18n } = useTranslation();
  const [artworks, setArtworks] = useState<FramingArtwork[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ shopifyId, locale: i18n.language });

    fetch(`/api/storyblok/framing-artworks?${params}`)
      .then((res) => res.json() as Promise<FramingArtworksResponse>)
      .then(({ artworks: next }) => !cancelled && setArtworks(next))
      .catch(() => !cancelled && setArtworks([]));

    return () => {
      cancelled = true;
    };
  }, [i18n.language, shopifyId]);

  return artworks;
};

/** Whether a work's loaded purchase details offer any framing. */
export const hasFramingOptions = (details: Details | undefined) =>
  Boolean(details?.frames.length);

/** Live Shopify purchase details per product, fetched once each. Shared with
    the product page modal. */
export const usePurchaseDetails = (shopifyIds: string[]) => {
  const [detailsById, setDetailsById] = useState<Record<string, Details>>({});

  useEffect(() => {
    shopifyIds
      .filter((id) => !(id in detailsById))
      .forEach((id) => {
        shopifyProductsGateway
          .getProductPurchaseDetails({
            frameSource: 'currentProduct',
            identifier: { id },
            releaseName: null,
          })
          .catch(() => null)
          .then((details) =>
            setDetailsById((current) => ({ ...current, [id]: details })),
          );
      });
    // Only new ids trigger a fetch; results landing must not refetch.
  }, [shopifyIds.join()]);

  return detailsById;
};

const FramingPreviewer = ({
  blok,
  defaultShopifyId,
}: {
  blok: FramingPreviewerStoryblok;
  defaultShopifyId: string;
}) => {
  const { i18n, t } = useTranslation('framing');
  const [selectedId, setSelectedId] = useState(defaultShopifyId);
  const storyArtworks = useArtworks(defaultShopifyId);
  // Every listed work's details, so works without framing stay out of the
  // dropdown.
  const detailsById = usePurchaseDetails([
    defaultShopifyId,
    ...(storyArtworks ?? []).map(({ id }) => id),
  ]);
  const defaultDetails = detailsById[defaultShopifyId];
  const details = detailsById[selectedId];
  const frameOptions = useMemo(
    () => (details ? toFrameOptions(details) : []),
    [details],
  );

  // A product unknown to Storyblok still previews on its own.
  const artworks = useMemo((): FramingArtwork[] => {
    if (storyArtworks?.length) {
      return storyArtworks.filter(
        ({ id }) =>
          id === defaultShopifyId || hasFramingOptions(detailsById[id]),
      );
    }
    if (!defaultDetails) return [];

    return [
      {
        id: defaultShopifyId,
        title: defaultDetails.name,
        thumbnail: {
          src: defaultDetails.image ?? '',
          alt: defaultDetails.name,
        },
      },
    ];
  }, [defaultDetails, defaultShopifyId, detailsById, storyArtworks]);

  // The selected work's product page, which opens with the frame chosen here.
  const selectedSlug = artworks.find(({ id }) => id === selectedId)?.slug;

  // A missing, unpublished or unframeable product renders nothing rather than
  // an empty shell.
  if (defaultDetails === null || defaultDetails?.frames.length === 0) {
    return null;
  }

  return (
    <section className="grid gap-9 md:gap-12">
      <ContentBlockTitle
        blok={{
          _uid: blok._uid,
          component: 'title',
          description: blok.description,
          title: blok.title || t('previewer.title'),
          titleSize: blok.titleSize || 'lg',
        }}
        headingClassName="md:tracking-[-0.02em]"
      />
      {details && storyArtworks !== null ? (
        <FrameSelection
          key={selectedId}
          artworks={artworks}
          currencyCode={details.currency}
          frameOptions={frameOptions}
          layout="inline"
          onArtworkChange={setSelectedId}
          productImage={details.image}
          renderActions={
            selectedSlug
              ? (draft) => (
                  <Button
                    as="a"
                    fullWidth
                    href={withFramingChoice(
                      getLocalisedProductUrl(selectedSlug, i18n.language),
                      draft.draftFrame,
                    )}
                    label={t('previewer.viewWork')}
                    variant="primary"
                  />
                )
              : undefined
          }
          selectedArtworkId={selectedId}
        />
      ) : (
        // Loading, or the selected variant failed to load: keep the dropdown
        // so another artwork can still be picked.
        <FrameDrawer
          artworks={artworks}
          groups={[]}
          isLoading={details === undefined || storyArtworks === null}
          layout="inline"
          onArtworkChange={setSelectedId}
          onSelect={noop}
          selectedArtworkId={selectedId}
          total={0}
        />
      )}
    </section>
  );
};

/**
 * Release page framing previewer. Product pages open the same selection from
 * the purchase panel instead (`ProductFramingCustomise`).
 */
export const ContentBlockFramingPreviewer = ({
  blok,
}: {
  blok: FramingPreviewerStoryblok;
}) => {
  const enabled = useFeatureIsOn(FF_FRAMING_PREVIEWER_RELEASE_PAGE);
  const shopifyId = resolveShopifyAdminProductId(blok.product?.[0]?.id);

  if (!enabled || !shopifyId) return null;

  return (
    <FramingPreviewer
      key={shopifyId}
      blok={blok}
      defaultShopifyId={shopifyId}
    />
  );
};
